/**
 * Custom Visualization Type Registry
 * ===================================
 * Lets internal teams or third-party plugins register new viz types without touching core code.
 *
 * A viz registration consists of three optional pieces:
 *   1. `renderer`   — React component that draws the visual given { data, config, ...rendererProps }
 *   2. `configPanel`— React component for the right-side Format panel (receives { cfg, set, currentVisual, allFields, ... })
 *   3. `meta`       — UX metadata: label, icon, defaultConfig, supportsTopN, supportsPeriodComparison, etc.
 *
 * Registered viz types are merged with the built-in ones at runtime — both inside
 * the report builder's ConfigPanel and the visual renderer dispatcher.
 *
 * Usage (place this in any module imported by the app, e.g. /src/plugins/index.js):
 *
 *   import { registerVizType } from '@/lib/customVizRegistry';
 *   registerVizType({
 *     type: 'sankey',
 *     label: 'Sankey Diagram',
 *     icon: GitBranch,                   // any lucide-react icon
 *     renderer: SankeyRenderer,
 *     configPanel: SankeyConfigPanel,
 *     meta: {
 *       defaultConfig: { palette: 'cool', curve: 0.5 },
 *       supportsTopN: false,
 *       supportsPeriodComparison: false,
 *       supportsDrillThrough: true,
 *       shelves: ['source','target','value'],   // hints which drop shelves are needed
 *     },
 *   });
 *
 * Built-in viz types take precedence over registered ones with the same `type` key,
 * preventing a plugin from silently overriding core behaviour.
 */
import { useEffect, useMemo, useState } from 'react';

// Reserved type keys that ship with INZARA. Plugins cannot override these.
const BUILT_IN_TYPES = new Set([
  'table', 'pivot', 'loss_triangle',
  'bar_chart', 'line_chart', 'area_chart', 'pie_chart', 'combo',
  'scatter', 'gauge', 'funnel', 'treemap', 'heatmap', 'waterfall', 'geo_map',
  'wordcloud',
  'kpi', 'ai_text', 'image', 'rich_text', 'shape',
]);

const _registry = new Map();
const _listeners = new Set();

function _notify() {
  _listeners.forEach(fn => { try { fn(); } catch {} });
}

/**
 * Register a new viz type.
 * Throws if `type` collides with a built-in or has already been registered.
 */
export function registerVizType(definition) {
  if (!definition || typeof definition !== 'object') {
    throw new Error('registerVizType: definition must be an object');
  }
  const { type, label, icon, renderer, configPanel, meta } = definition;
  if (!type || typeof type !== 'string') {
    throw new Error('registerVizType: `type` (string) is required');
  }
  if (BUILT_IN_TYPES.has(type)) {
    throw new Error(`registerVizType: cannot override built-in viz type "${type}"`);
  }
  if (_registry.has(type)) {
    throw new Error(`registerVizType: viz type "${type}" already registered`);
  }
  _registry.set(type, {
    type,
    label: label || type,
    icon: icon || null,
    renderer: renderer || null,
    configPanel: configPanel || null,
    meta: meta || {},
  });
  _notify();
  return () => unregisterVizType(type);
}

export function unregisterVizType(type) {
  const removed = _registry.delete(type);
  if (removed) _notify();
  return removed;
}

export function getRegisteredVizType(type) {
  return _registry.get(type) || null;
}

export function getRegisteredVizTypes() {
  return Array.from(_registry.values());
}

export function getRegisteredVizTypeMap() {
  // Returns a fresh plain object snapshot keyed by type — useful for ConfigPanel TYPE_RENDERERS merge.
  const out = {};
  for (const [k, v] of _registry.entries()) out[k] = v;
  return out;
}

export function isBuiltInVizType(type) {
  return BUILT_IN_TYPES.has(type);
}

/**
 * Subscribe to registry changes. Returns an unsubscribe fn.
 * Useful for React components that want to re-render when plugins load.
 */
export function subscribeToRegistry(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/**
 * React hook that re-renders when the registry changes.
 * Returns a fresh array of registrations each time.
 */
export function useCustomVizTypes() {
  const [version, setVersion] = useState(0);
  useEffect(() => subscribeToRegistry(() => setVersion(v => v + 1)), []);
  // Touch version so React knows this is a dependency
  return useMemo(() => getRegisteredVizTypes(), [version]); // eslint-disable-line react-hooks/exhaustive-deps
}
